export const colors = {
  // Primary palette
  tabbioViolet: '#5e29ff',
  synergyLavender: '#9b7aff',
  novaGradientStart: '#5e29ff',
  novaGradientEnd: '#3B82F6',

  // Brand accent violet: the Figma "brand violet" used for onboarding and
  // Pro/upgrade affordances. Distinct from `primary`/`tabbioViolet` (#5e29ff);
  // `brandVioletLight` is the lighter stop of the Pro upgrade-badge gradient.
  brandViolet: '#5A2AFF',
  brandVioletLight: '#8467FD',

  // Functional
  primary: '#5e29ff',
  secondary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',

  // UAE PASS brand green, used to label genuinely verified UAE PASS data in
  // Settings. NOT the CV shield: that mark is monochrome and inherits the name's
  // own text color (see `resolveUaePassMarkOpacity` in @tabbio/public-renderers)
  // so it reads as part of the name on every template and appearance.
  uaePassVerified: '#00B74F',

  // Neutral (OpenAI-inspired)
  gray: {
    50: '#f9f9f9',
    100: '#ededed',
    200: '#dfdfdf',
    300: '#c4c4c4',
    400: '#8f8f8f',
    500: '#5d5d5d',
    600: '#414141',
    700: '#303030',
    800: '#212121',
    900: '#181818',
    950: '#0d0d0d',
  },
};

// Primary color shades (tints & shades of #5e29ff)
export const primaryShades = {
  50: '#f3efff',
  100: '#e4daff',
  200: '#c9b5ff',
  300: '#ae8fff',
  400: '#8a5cff',
  500: '#5e29ff',
  600: '#4d1fe6',
  700: '#3d18b3',
  800: '#2d1280',
  900: '#1e0c4d',
};

// Alpha transparency utilities
export const alpha = {
  black02: 'rgba(0,0,0,0.02)',
  black05: 'rgba(0,0,0,0.05)',
  black08: 'rgba(0,0,0,0.08)',
  black10: 'rgba(0,0,0,0.10)',
  black12: 'rgba(0,0,0,0.12)',
  black15: 'rgba(0,0,0,0.15)',
  black20: 'rgba(0,0,0,0.20)',
  black50: 'rgba(0,0,0,0.50)',
  white02: 'rgba(255,255,255,0.02)',
  white05: 'rgba(255,255,255,0.05)',
  white06: 'rgba(255,255,255,0.06)',
  white08: 'rgba(255,255,255,0.08)',
  white10: 'rgba(255,255,255,0.10)',
  white12: 'rgba(255,255,255,0.12)',
  white15: 'rgba(255,255,255,0.15)',
  white20: 'rgba(255,255,255,0.20)',
  white50: 'rgba(255,255,255,0.50)',
};

export const lightTheme = {
  // Surfaces
  background: '#ffffff',
  backgroundButton: '#ffffff',
  surface: '#f9f9f9',
  surfaceSecondary: '#ededed',
  surfaceTertiary: '#dfdfdf',
  surfaceElevated: '#ffffff',

  // Text
  text: '#0d0d0d',
  textSecondary: '#373737',
  textTertiary: '#656565',

  // Borders (alpha-based: adapts to any background)
  border: 'rgba(0,0,0,0.10)',
  borderSubtle: 'rgba(0,0,0,0.05)',
  borderStrong: 'rgba(0,0,0,0.15)',

  // Chat
  chatBubbleUser: '#ededed',
  chatBubbleAI: 'transparent',

  // Button semantics
  buttonPrimaryBg: '#5e29ff',
  buttonPrimaryText: '#ffffff',
  buttonSoftBg: '#f3efff',
  buttonSoftText: '#5e29ff',
  buttonOutlineBorder: 'rgba(0,0,0,0.15)',
  buttonGhostHover: 'rgba(0,0,0,0.05)',
  // Contrast CTA (onboarding "Next" etc.): near-black on light, inverts on dark
  buttonContrastBg: '#000000',
  buttonContrastText: '#ffffff',

  // Focus
  focusRing: '#5e29ff',

  // Semantic status
  success: '#10B981',
  successSoft: 'rgba(16,185,129,0.08)',
  successBorder: 'rgba(16,185,129,0.3)',
  danger: '#EF4444',
  dangerSoft: 'rgba(239,68,68,0.08)',

  // CV Health: the one scoped colour exception on an otherwise neutral
  // surface. The three rings (and their row icons) carry one hue per reading
  // audience; the warm attention pill marks open suggestions. Mirrored as
  // `--health-*` in apps/mobile/global.css.
  healthPeople: colors.primary,
  healthSystems: '#3B82F6',
  healthAi: '#14B8A6',
  healthTrack: 'rgba(0,0,0,0.08)',
  healthAttentionBg: '#FDECDC',
  healthAttentionText: '#9A4B12',
  healthInk: '#202124',
  healthInkSoft: '#55565a',
  healthInkMuted: '#737478',
  // Gauge report: accent (progress, active step), passed check, the gauge's
  // empty arc, the score card's hairline and the grouped surface.
  healthAccent: colors.primary,
  healthGood: '#247248',
  healthGaugeTrack: 'rgba(0,0,0,0.07)',
  healthLine: 'rgba(0,0,0,0.08)',
  healthSurface: 'rgba(0,0,0,0.04)',
  // The score card's glass edge (mirrors --health-glass-shadow in
  // apps/mobile/global.css): drop shadow, inset top highlight, inset bottom shade.
  healthGlassDrop: 'rgba(0,0,0,0.035)',
  healthGlassHighlight: 'rgba(255,255,255,0.8)',
  healthGlassShade: 'rgba(0,0,0,0.05)',
};

export const darkTheme = {
  // Surfaces
  background: '#000000',
  backgroundButton: 'rgba(43,43,43,0.53)',
  surface: '#181818',
  surfaceSecondary: '#212121',
  surfaceTertiary: '#303030',
  surfaceElevated: '#1c1c1c',

  // Text
  text: '#ffffff',
  textSecondary: '#afafaf',
  textTertiary: '#8f8f8f',

  // Borders (alpha-based)
  border: 'rgba(255,255,255,0.12)',
  borderSubtle: 'rgba(255,255,255,0.06)',
  borderStrong: 'rgba(255,255,255,0.20)',

  // Chat
  chatBubbleUser: '#303030',
  chatBubbleAI: 'transparent',

  // Button semantics
  buttonPrimaryBg: '#5e29ff',
  buttonPrimaryText: '#ffffff',
  buttonSoftBg: 'rgba(94,41,255,0.15)',
  buttonSoftText: '#c9b5ff',
  buttonOutlineBorder: 'rgba(255,255,255,0.15)',
  buttonGhostHover: 'rgba(255,255,255,0.05)',
  // Contrast CTA inverts on dark: white surface, black label
  buttonContrastBg: '#ffffff',
  buttonContrastText: '#000000',

  // Focus
  focusRing: '#8a5cff',

  // Semantic status
  success: '#34D399',
  successSoft: 'rgba(52,211,153,0.12)',
  successBorder: 'rgba(52,211,153,0.3)',
  danger: '#F87171',
  dangerSoft: 'rgba(248,113,113,0.12)',

  // CV Health (see lightTheme): contrast-safe tints of the same three hues.
  healthPeople: primaryShades[400],
  healthSystems: '#60A5FA',
  healthAi: '#2DD4BF',
  healthTrack: 'rgba(255,255,255,0.12)',
  healthAttentionBg: 'rgba(249,115,22,0.20)',
  healthAttentionText: '#F8B98A',
  healthInk: '#f6f6f7',
  healthInkSoft: '#c0c0c6',
  healthInkMuted: '#96969f',
  healthAccent: primaryShades[400],
  healthGood: '#80D7AE',
  healthGaugeTrack: 'rgba(255,255,255,0.10)',
  healthLine: 'rgba(255,255,255,0.08)',
  healthSurface: 'rgba(255,255,255,0.05)',
  healthGlassDrop: 'rgba(0,0,0,0.125)',
  healthGlassHighlight: 'rgba(255,255,255,0.12)',
  healthGlassShade: 'rgba(255,255,255,0.04)',
};

// Onboarding gradients: raw Figma values (not part of the semantic palette).
// Tuples are `as const` so they satisfy expo-linear-gradient's strict color tuple type.
export const gradients = {
  // Welcome/onboarding background: violet → lavender → white (top → bottom)
  onboardingBgLight: ['#5A2AFF', '#AB94F8', '#FFFFFF'] as const,
  onboardingBgLightLocations: [0, 0.14, 0.34] as const,
  // Dark derivation: violet glow → deep violet → near-black
  onboardingBgDark: ['#5A2AFF', '#2D1280', '#0D0D0D'] as const,
  onboardingBgDarkLocations: [0, 0.45, 0.85] as const,
  // Progress-bar fill (multi-stop, left → right)
  onboardingProgress: ['#A6B7FF', '#9BE2FE', '#AF96FF', '#5A2AFF'] as const,
};
